import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, ArrowRight, Filter, Calendar, User, LayoutGrid, X, GraduationCap, Sparkles, AlertCircle, Trophy } from "lucide-react";
import { usePlanning } from "../lib/usePlanning";
import {
  timeToMin, durationLabel, AGE_BUCKETS, matchesAgeBucket,
  disciplineColor, LEVEL_DESCRIPTIONS, courseSlug, seatState, MESSAGE_ESSAI_COMPLET,
} from "../lib/planningHelpers";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { coursesSchema } from "../lib/schema";
import { inscriptionPourLaRentree, MESSAGE_INSCRIPTION_TARDIVE, LABEL_PLACES_RENTREE } from "../lib/dates";
import { AUDITION, auditionPassee } from "../lib/auditions";
import { useReglages } from "../lib/useReglages";


export default function Planning() {
  const { courses, filters, semestre, loading, error, secours, secours_le, reload } = usePlanning();
  // La date de l'audition vient de la console (`/api/reglages`) : le hook l'applique à
  // AUDITION et fait redessiner la page quand elle arrive.
  useReglages();
  // Les horaires de secours datant du jour même sont les BONS horaires : les cours ne
  // changent pas d'un jour à l'autre. On n'affiche donc AUCUN message — il inquiétait
  // pour rien. Seul un relevé plus ancien mérite un avertissement.
  const secoursDuJour = secours_le === new Date().toISOString().slice(0, 10);
  const coursesJsonLd = useMemo(() => coursesSchema(courses), [courses]);
  useSEO({
    title: "Planning des cours de danse — 1000 ARTS Cugy VD",
    description: "Consultez le planning hebdomadaire des cours de danse à 1000 ARTS Cugy. Hip-hop, ballet, comédie musicale, heels, salsa, zumba du lundi au samedi. Filtrez par discipline et par tranche d'âge.",
    canonical: "/planning",
    jsonLd: coursesJsonLd,
  });


  const [mode, setMode] = useState("voir-tout");
  const [day, setDay] = useState("");
  const [discFilter, setDiscFilter] = useState("Toutes");
  const [ageFilter, setAgeFilter] = useState("Toutes");

  const days = filters.days || [];
  const activeDay = day || days[0] || "";

  const [modalCourse, setModalCourse] = useState(null);
  const navigate = useNavigate();
  const closeModal = () => setModalCourse(null);
  const goToForm = (type) => {
    if (!modalCourse) return;
    navigate(`/inscription?type=${type}&course=${encodeURIComponent(modalCourse.id)}`);
    setModalCourse(null);
  };

  const keep = (c) =>
    (discFilter === "Toutes" || c.discipline === discFilter) &&
    (ageFilter === "Toutes" || matchesAgeBucket(c, ageFilter));

  const byDay = useMemo(() => {
    const grouped = {};
    days.forEach((d) => {
      grouped[d] = courses
        .filter((c) => c.day === d)
        .filter(keep)
        .sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
    });
    return grouped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, days, discFilter, ageFilter]);

  const dayItems = byDay[activeDay] || [];
  const totalWeek = days.reduce((n, d) => n + (byDay[d]?.length || 0), 0);

  return (
    <div data-testid="page-planning" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">{semestre || "Saison 2026 / 2027"}</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
        Planning des<br /><span style={{ color: "var(--primary)" }}>cours</span>
      </h1>
      <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl">
        Toutes les salles réunies. Filtre par jour, discipline ou tranche d'âge. Clique sur un cours pour découvrir sa description ; clique sur « S'inscrire » pour réserver ton essai ou ton semestriel.
      </p>

      {loading && (
        <div className="card-flat mt-10 text-center text-[var(--muted)]" data-testid="planning-loading">
          Chargement du planning…
        </div>
      )}

      {error && (
        <div className="card-flat mt-10 flex items-start gap-3" data-testid="planning-error" style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}>
          <AlertCircle size={20} style={{ color: "#DC2626" }} className="shrink-0 mt-0.5" />
          <div className="text-sm text-[#991B1B]">
            Le planning est momentanément indisponible.
            <button data-testid="planning-retry" onClick={reload} className="underline font-semibold ml-1">Réessayer</button>
            {" "}ou écris-nous à <a href="mailto:info1000arts@gmail.com" className="underline">info1000arts@gmail.com</a>.
          </div>
        </div>
      )}

      {secours && !secoursDuJour && (
        <div className="card-flat mt-10 flex items-start gap-3" data-testid="planning-secours"
          style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
          <AlertCircle size={20} style={{ color: "#B45309" }} className="shrink-0 mt-0.5" />
          <div className="text-sm text-[#78350F]">
            Voici les horaires du{" "}
            <strong>{secours_le ? secours_le.split("-").reverse().join(".") : "dernier relevé"}</strong>.
            Notre serveur est momentanément injoignable : les inscriptions en ligne et les
            places disponibles ne sont pas à jour.
            <button data-testid="planning-retry-secours" onClick={reload} className="underline font-semibold ml-1">Réessayer</button>
            {" "}ou écris-nous à <a href="mailto:info1000arts@gmail.com" className="underline">info1000arts@gmail.com</a>.
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
      {/* MODE TOGGLE */}
      <div className="flex flex-wrap gap-2 mt-10" data-testid="planning-mode">
        <button data-testid="mode-voir-tout" onClick={() => setMode("voir-tout")} className={`pill ${mode === "voir-tout" ? "active" : ""}`}>
          <LayoutGrid size={14} className="inline mr-1" /> Semaine
        </button>
        <button data-testid="mode-day" onClick={() => setMode("day")} className={`pill ${mode === "day" ? "active" : ""}`}>
          <Calendar size={14} className="inline mr-1" /> Par jour
        </button>
      </div>

      {/* DAY TABS */}
      {mode === "day" && (
        <div className="flex flex-wrap gap-2 mt-6 pb-2 border-b border-[var(--border-soft)]">
          {days.map((d) => (
            <button key={d} data-testid={`day-tab-${d.toLowerCase()}`} onClick={() => setDay(d)} className={`pill ${d === activeDay ? "active" : ""}`}>{d}</button>
          ))}
        </div>
      )}

      {/* FILTRES — tous construits depuis les valeurs présentes dans le Sheet */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="planning-filter-cat">
        <Filter size={16} style={{ color: "var(--primary)" }} />
        <span className="eyebrow text-[10px] mr-1">Discipline</span>
        <button data-testid="cat-filter-toutes" onClick={() => setDiscFilter("Toutes")} className={`pill ${discFilter === "Toutes" ? "active" : ""}`}>
          Toutes
        </button>
        {(filters.disciplines || []).map((d, i) => {
          const color = disciplineColor(d, i);
          return (
            <button
              key={d}
              data-testid={`cat-filter-${slugify(d)}`}
              onClick={() => setDiscFilter(d)}
              className={`pill ${discFilter === d ? "active" : ""}`}
              style={discFilter === d ? { background: color, color: "#0F1B2D", borderColor: color } : { borderLeft: `4px solid ${color}` }}
            >
              {d}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="age-chips">
        <span className="eyebrow text-[10px] mr-1">Âge</span>
        {[["Toutes", "Tous âges", ""], ...Object.entries(AGE_BUCKETS).map(([k, v]) => [k, v.label, v.sub])].map(([k, label, sub]) => (
          <button key={k} data-testid={`age-chip-${k}`} onClick={() => setAgeFilter(k)} className={`pill ${ageFilter === k ? "active" : ""}`}>
            {label}{sub && <span className="text-[10px] ml-1 opacity-70">{sub}</span>}
          </button>
        ))}
      </div>

      {/* VUE SEMAINE */}
      {mode === "voir-tout" && (
        <div className="mt-10" data-testid="planning-week">
          {totalWeek === 0 ? (
            <div className="card-flat text-center text-[var(--muted)]">Aucun cours ne correspond à ce filtre.</div>
          ) : (
            <>
              <div className="md:hidden space-y-4" data-testid="planning-week-mobile">
                {days.map((d, idx) => {
                  const items = byDay[d] || [];
                  if (items.length === 0) return null;
                  const dayColors = [
                    { bg: "linear-gradient(135deg,#0EA5B7 0%,#22D3EE 100%)", accent: "#0EA5B7" },
                    { bg: "linear-gradient(135deg,#2563EB 0%,#60A5FA 100%)", accent: "#2563EB" },
                    { bg: "linear-gradient(135deg,#7C3AED 0%,#A78BFA 100%)", accent: "#7C3AED" },
                    { bg: "linear-gradient(135deg,#EC4899 0%,#F472B6 100%)", accent: "#EC4899" },
                    { bg: "linear-gradient(135deg,#F97316 0%,#FB923C 100%)", accent: "#F97316" },
                    { bg: "linear-gradient(135deg,#10B981 0%,#34D399 100%)", accent: "#10B981" },
                  ];
                  const color = dayColors[idx % dayColors.length];
                  return (
                    <div
                      key={d}
                      data-testid={`mobile-day-${d.toLowerCase()}`}
                      className="rounded-3xl overflow-hidden shadow-md"
                      style={{ background: "#fff", border: "1px solid var(--border-soft)" }}
                    >
                      <div
                        className="px-5 py-3 text-white font-display text-lg tracking-widest uppercase flex items-center justify-between"
                        style={{ background: color.bg }}
                      >
                        <span>{d}</span>
                        <span className="text-xs font-normal opacity-90">{items.length} cours</span>
                      </div>
                      <div className="p-4">
                        <ul className="space-y-1.5">
                          {items.map((s) => {
                            const slug = courseSlug(s);
                            const state = seatState(s);
                            const inner = (
                              <>
                                <span className="font-mono font-bold text-sm shrink-0" style={{ color: color.accent, minWidth: 46 }}>
                                  {s.start.replace(":", "h")}
                                </span>
                                <span className="flex-1 text-sm text-[var(--text)] leading-snug min-w-0">
                                  <span className="font-medium">{s.name}</span>
                                  <span className="text-[var(--muted)] text-[13px]"> · {s.age_label}</span>
                                  <span className="text-[var(--muted)] text-[12px]"> {durationLabel(s.duration_min)}</span>
                                  <span className="block text-[var(--muted)] text-[11px] mt-0.5">
                                    {s.room}{(s.teachers || []).length > 0 ? ` · ${s.teachers.join(", ")}` : ""}
                                    {s.level ? ` · Niveau ${s.level}` : ""}
                                  </span>
                                </span>
                              </>
                            );
                            return (
                              <li key={s.id}>
                                <div className="flex items-baseline gap-3 py-1.5 -mx-1 px-1 rounded-lg">
                                  {slug ? (
                                    <Link to={`/cours/${slug}`} className="flex items-baseline gap-3 flex-1 min-w-0 active:bg-[var(--primary-tint)] rounded-lg" data-testid={`mobile-row-${s.id}`}>
                                      {inner}
                                    </Link>
                                  ) : (
                                    <div className="flex items-baseline gap-3 flex-1 min-w-0" data-testid={`mobile-row-${s.id}`}>{inner}</div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => state.bookable || state.waitlist ? setModalCourse(s) : null}
                                    disabled={!state.bookable && !state.waitlist}
                                    data-testid={`mobile-inscribe-${s.id}`}
                                    className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0 disabled:opacity-50"
                                    style={{ background: inscriptionPourLaRentree(s) ? "#1D4ED8" : state.waitlist ? "#B45309" : color.accent, color: "#fff" }}
                                    aria-label={`S'inscrire à ${s.name}`}
                                  >
                                    {inscriptionPourLaRentree(s) ? "Rentrée" : state.waitlist ? "Liste d'attente" : "S'inscrire"}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden md:grid grid-cols-3 lg:grid-cols-6 gap-4">
              {days.map((d) => (
                <div key={d} data-testid={`week-col-${d.toLowerCase()}`} className="flex flex-col">
                  <div className="sticky top-[88px] z-30 pb-3 mb-3 border-b-2 -mx-1 px-1 pt-3" style={{ borderColor: "var(--primary)", background: "var(--bg)" }}>
                    <div className="font-display text-lg tracking-wider uppercase">{d}</div>
                    <div className="text-[11px] text-[var(--muted)] mt-0.5">{(byDay[d] || []).length} cours</div>
                  </div>
                  <div className="flex flex-col gap-3">
                    {(byDay[d] || []).length === 0 ? (
                      <div className="text-xs text-[var(--muted)] italic py-3">— Aucun cours —</div>
                    ) : (
                      (byDay[d] || []).map((s) => <SessionCard key={s.id} s={s} onInscription={setModalCourse} />)
                    )}
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* VUE PAR JOUR */}
      {mode === "day" && (
        <div className="mt-10" data-testid="planning-list">
          <div className="pb-3 mb-5 border-b-2" style={{ borderColor: "var(--primary)" }}>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">{activeDay}</h2>
            <div className="text-sm text-[var(--muted)] mt-1">{dayItems.length} cours</div>
          </div>
          {dayItems.length === 0 && <div className="card-flat text-center text-[var(--muted)]">Aucun cours ne correspond à ce filtre.</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dayItems.map((s) => <SessionCard key={s.id} s={s} onInscription={setModalCourse} />)}
          </div>
        </div>
      )}

      {/* LÉGENDE */}
      <section className="mt-16 grid md:grid-cols-2 gap-6" data-testid="planning-legend">
        <div className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
          <div className="eyebrow mb-3">Niveaux</div>
          <ul className="space-y-1.5 text-sm">
            {(filters.levels || []).map((l) => (
              <li key={l}><strong>{l}</strong>{LEVEL_DESCRIPTIONS[l] ? ` — ${LEVEL_DESCRIPTIONS[l]}` : ""}</li>
            ))}
          </ul>
        </div>
        <div className="card-flat">
          <div className="eyebrow mb-3">Disciplines</div>
          <div className="flex flex-wrap gap-2">
            {(filters.disciplines || []).map((d, i) => (
              <span key={d} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: disciplineColor(d, i), color: "#0F1B2D" }}>{d}</span>
            ))}
          </div>
        </div>
      </section>
        </>
      )}

      {/* CTA */}
      <section className="mt-12 card-flat text-center" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "48px 32px" }}>
        <h3 className="font-display text-3xl md:text-4xl">Prêt·e à sauter le pas&nbsp;?</h3>
        <p className="text-white/90 mt-3">Choisis ton cours et inscris-toi en essai ou en semestriel — c'est toi qui décides.</p>
        <Link to="/inscription" data-testid="planning-final-cta" className="inline-flex items-center gap-2 mt-6 px-8 py-4 rounded-full bg-white text-[var(--primary)] font-semibold">
          S'inscrire <ArrowRight size={16} />
        </Link>
      </section>

      {/* MODAL */}
      {modalCourse && (
        <div
          data-testid="inscription-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(15,27,45,0.6)", backdropFilter: "blur(4px)" }}
          onClick={closeModal}
        >
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeModal}
              data-testid="inscription-modal-close"
              aria-label="Fermer"
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--bg-soft)]"
            >
              <X size={18} />
            </button>
            <div className="eyebrow mb-2">Inscription</div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">{modalCourse.name}</h3>
            <p className="text-sm text-[var(--muted)] mb-2">
              {modalCourse.day} · {modalCourse.start} – {modalCourse.end} · {modalCourse.room}
            </p>
            {(inscriptionPourLaRentree(modalCourse) || modalCourse.message_site) && (
              <p className="text-sm font-medium mb-4" data-testid="modal-seat-message" style={{ color: inscriptionPourLaRentree(modalCourse) ? "#1D4ED8" : seatState(modalCourse).urgent ? "#B45309" : "var(--primary)" }}>
                {inscriptionPourLaRentree(modalCourse) ? MESSAGE_INSCRIPTION_TARDIVE : modalCourse.message_site}
              </p>
            )}
            <p className="text-[var(--text)] mb-6 leading-relaxed">
              Comment souhaites-tu t'inscrire à ce cours&nbsp;?
            </p>
            <div className="space-y-3">
              {modalCourse.discipline === "Compétition" ? (
                <>
                  {/* Audition passée : plus de date annoncée ni de bouton, le formulaire est
                      fermé. L'école pose la date suivante dans la console. */}
                  {!auditionPassee() && (
                  <button
                    type="button"
                    onClick={() => navigate("/inscription/audition", {
                      state: { groupe: /compagnie/i.test(modalCourse.name || "") ? "Compagnie" : "Compétition" },
                    })}
                    data-testid="inscription-modal-audition"
                    className="w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all hover:shadow-md hover:scale-[1.01]"
                    style={{ background: "#FFF7ED", border: "1.5px solid #F97316" }}
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "#F97316", color: "#fff" }}>
                      <Trophy size={22} />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg leading-tight">S'inscrire à l'audition</div>
                      <div className="text-xs text-[var(--muted)] mt-1">
                        Audition du {AUDITION.date_label.charAt(0).toLowerCase() + AUDITION.date_label.slice(1)} — c'est la porte d'entrée de ce groupe.
                      </div>
                    </div>
                    <ArrowRight size={18} style={{ color: "#F97316" }} />
                  </button>
                  )}
                  <div
                    className="p-4 rounded-2xl text-xs leading-relaxed"
                    data-testid="modal-competition-note"
                    style={{ background: "#FFFBEB", color: "#7C2D12" }}
                  >
                    🏆 Ce groupe ne se découvre pas en cours d'essai. L'inscription au semestre ci-dessous
                    est réservée aux danseurs déjà sélectionnés lors d'une audition.
                  </div>
                </>
              ) : seatState(modalCourse).waitlist ? (
                /* Cours complet : pas de cours d'essai (école, 02.11.2026), la liste
                   d'attente juste en dessous reste ouverte. */
                <div
                  className="p-4 rounded-2xl text-sm leading-relaxed"
                  data-testid="modal-essai-complet"
                  style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }}
                >
                  {MESSAGE_ESSAI_COMPLET}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => goToForm("essai")}
                  data-testid="inscription-modal-essai"
                  className="w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all hover:shadow-md hover:scale-[1.01]"
                  style={{ background: "var(--primary-tint)", border: "1.5px solid var(--primary)" }}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary)", color: "#fff" }}>
                    <Sparkles size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-lg leading-tight">Cours d'essai</div>
                    <div className="text-xs text-[var(--muted)] mt-1">
                      Une séance découverte — gratuite pendant les Portes Ouvertes, CHF 10 sinon.
                    </div>
                  </div>
                  <ArrowRight size={18} style={{ color: "var(--primary)" }} />
                </button>
              )}
              <button
                type="button"
                onClick={() => goToForm("semestriel")}
                data-testid="inscription-modal-semestriel"
                className="w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all hover:shadow-md hover:scale-[1.01]"
                style={{ background: "#fff", border: "1.5px solid var(--border-soft)" }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--text)", color: "#fff" }}>
                  <GraduationCap size={22} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg leading-tight">
                    {inscriptionPourLaRentree(modalCourse)
                      ? "Inscription pour la rentrée de fin août"
                      : seatState(modalCourse).waitlist
                      ? "Complet — s'inscrire en liste d'attente"
                      : "Inscription semestrielle"}
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-1">
                    {inscriptionPourLaRentree(modalCourse)
                      ? "Ce cours ne prend plus de nouvel élève d'ici juin : ta demande est enregistrée pour la rentrée de fin août."
                      : seatState(modalCourse).waitlist
                      ? "Ta demande sera enregistrée en liste d'attente et l'école te recontactera dès qu'une place se libère."
                      : "Réserve ta place pour le semestre complet — dès CHF 200."}
                  </div>
                </div>
                <ArrowRight size={18} style={{ color: "var(--text)" }} />
              </button>
            </div>
            <p className="text-xs text-[var(--muted)] italic mt-6 text-center">
              Le cours sera automatiquement pré-sélectionné dans le formulaire.
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function SessionCard({ s, onInscription }) {
  const color = disciplineColor(s.discipline);
  const slug = courseSlug(s);
  const state = seatState(s);
  const testId = `session-${s.id}`;
  return (
    <div
      data-testid={testId}
      className="group relative rounded-2xl bg-white border border-[var(--border-soft)] p-5 flex flex-col gap-3 transition-all hover:shadow-lg hover:-translate-y-0.5"
      style={{ borderLeft: `4px solid ${color}`, opacity: state.hidden ? 0.5 : 1 }}
    >
      <span
        className="self-start inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide"
        style={{ background: `${color}22`, color }}
        data-testid={`${testId}-age`}
      >
        {s.age_label}
      </span>

      {slug ? (
        <Link
          to={`/cours/${slug}`}
          data-testid={`${testId}-title`}
          className="font-display text-xl md:text-[22px] leading-tight tracking-tight text-[var(--text)] group-hover:underline decoration-2 underline-offset-4"
          style={{ textDecorationColor: color }}
        >
          {s.name}
        </Link>
      ) : (
        <div data-testid={`${testId}-title`} className="font-display text-xl md:text-[22px] leading-tight tracking-tight text-[var(--text)]">{s.name}</div>
      )}

      <div className="flex items-center gap-2 text-[15px] font-medium text-[var(--text)]">
        <Clock size={14} style={{ color }} />
        <span>{s.start} <span className="text-[var(--muted)]">→</span> {s.end}</span>
        <span className="text-[12px] text-[var(--muted)]">{durationLabel(s.duration_min)}</span>
      </div>

      {s.level && (
        <div className="text-[12px] text-[var(--muted)] flex items-center gap-1.5">
          <span className="text-[10px] uppercase font-semibold tracking-wider" style={{ color }}>Niveau</span>
          <span className="text-[var(--text)] font-medium">{s.level}</span>
        </div>
      )}

      {(inscriptionPourLaRentree(s) || s.message_site) && (
        <div
          className="text-[11px] font-semibold px-2.5 py-1 rounded-full self-start"
          data-testid={`${testId}-seats`}
          style={inscriptionPourLaRentree(s)
            ? { background: "#DBEAFE", color: "#1D4ED8" }
            : state.urgent
            ? { background: "#FEF3C7", color: "#B45309" }
            : { background: "var(--bg-soft)", color: "var(--muted)" }}
        >
          {inscriptionPourLaRentree(s) ? LABEL_PLACES_RENTREE : s.message_site}
        </div>
      )}

      <div className="mt-auto pt-3 border-t border-[var(--border-soft)] flex items-start justify-between gap-2">
        <div className="text-[12px] text-[var(--muted)] flex items-start gap-1.5 flex-1 min-w-0">
          <User size={11} className="mt-[3px] shrink-0" />
          <span className="leading-snug break-words" data-testid={`${testId}-teacher`}>
            {(s.teachers || []).join(" · ") || "—"}
          </span>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)] shrink-0 mt-[3px]">
          {s.room}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onInscription(s)}
        disabled={!state.bookable && !state.waitlist}
        data-testid={`${testId}-inscription`}
        className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-2 rounded-full transition-all hover:brightness-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: inscriptionPourLaRentree(s) ? "#1D4ED8" : state.waitlist ? "#B45309" : color, color: inscriptionPourLaRentree(s) || state.waitlist ? "#fff" : "#0F1B2D" }}
      >
        {inscriptionPourLaRentree(s) ? "Réserver pour la rentrée" : state.waitlist ? "Complet — liste d'attente" : "S'inscrire"} <ArrowRight size={12} />
      </button>
    </div>
  );
}
